import {
    Form,
    Button,
    Select,
    Input,
    Result, 
    Skeleton, 
    Alert,
} from "antd";
import { useMemo, useState } from "react";
import { useForm } from "antd/lib/form/Form";
import {
    signularisApi,
    useCreateMetaDocumentMutation,
    useGetMetaDocumentsQuery,
    useGetRecipientsListQuery
} from "../../../store/api/signularisApi";
import { useAppDispatch } from "../../../hooks/redux";
import { CREATE_DOCUMENT_ROUTE, MAIN_PAGE_ROUTE } from "../../../routing/consts";
import { Link } from "react-router-dom";
import { DocTemplateType, Document, GetMetaDocumentsRequest } from "../../../types/types";
import genDocument8 from "../misc/gendoc8";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import style from '../CreateDocument.module.scss';
import { SendOutlined, LinkOutlined } from "@ant-design/icons";

const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

let guid = () => {
    let s4 = () => Math.floor((1 + Math.random()) * 0x100).toString(10).substring(1);
    return s4() + s4() + s4() + s4();
}

function styledDate() {
    const months = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", 
                    "августа", "сентября", "октября", "ноября", "декабря"]
    const dobj = new Date()
    return `${dobj.getDate()} ${months[dobj.getMonth()]} ${dobj.getFullYear()}`
}

type DocumentTitle1 = 'Ректору ГГНТУ \nМинцаеву М. Ш.' | 'Проректору по АХЧ \nЧегарбиеву А.С.' | 'Проректору по ЦиИ Пашаеву М.Я.'
const documentTitle1: DocumentTitle1[] = ['Ректору ГГНТУ \nМинцаеву М. Ш.', 'Проректору по АХЧ \nЧегарбиеву А.С.', 'Проректору по ЦиИ Пашаеву М.Я.']

function Template(prop : {referenceId?: string, referenceType?:string}) {
    const info = useSelector((state: RootState) => state.userinfo)
    const dispatch = useAppDispatch()
    const [form] = useForm()

    let reqParams: GetMetaDocumentsRequest = {
        type: 'signed',
        skip: 0,
        limit: 450,
        sort: 'newest'
    }
    
    const {data: docList, isLoading: docListIsLoading, isError: docListIsError, refetch: docListRefetch} = useGetMetaDocumentsQuery(reqParams)

    const docListFiltred: Document[] = useMemo(() => {
        if (docList?.docs && Array.isArray(docList.docs)) {
            return docList.docs.filter(item => item.documentType === 'technicalExaminationReport')
        }
        return [];
    }, [docList?.docs]);

    const {data: recipientsList} = useGetRecipientsListQuery()
    const [docname, setDocname] = useState("")

    const [createMetaDocument, {
        isLoading: mutationLoading,
        isSuccess: mutationSuccess,
    } ] = useCreateMetaDocumentMutation()

    const onFinish = async (values: any) => {
        const regnum = guid()
        setDocname('Заявка на приобретение')
        const formData = new FormData()
        formData.append("title", `Заявка на приобретение №${regnum}`)
        formData.append("brief", "Сгенерировано из шаблона")
        formData.append("recipients", values.recipients.join(","))
        formData.append("documentType", 'applicationForPurchase')
        
        const selectedDoc = docListFiltred[values.reference];
        const refType = prop.referenceType || selectedDoc?.documentType || 'default';
        const refId = prop.referenceId || selectedDoc?.id || '';

        formData.append("referenceType", refType)
        formData.append("reference", refId)

        values.regnum = regnum
        values.regdate = styledDate()
        const pdfBytes = await genDocument8(values, info)
        formData.append("file", new Blob([pdfBytes]))
        
        createMetaDocument(formData)
        dispatch(signularisApi.util.invalidateTags(['MetaDocument']))
    }

    if (mutationSuccess) return (
        <Result
            status="success"
            title="Заявка создана"
            subTitle={`Заявка на приобретение успешно сформирована.`}
            extra={<Link to={MAIN_PAGE_ROUTE + '/' + CREATE_DOCUMENT_ROUTE}><Button type="primary">Создать еще</Button></Link>}
        />
    );

    if (docListIsLoading) return <div style={{padding: '20px'}}><Skeleton active /><Skeleton active /></div>;

    return (
        <div className={style.generatorWrapper}>
            <h2 className={style.title}>Заявка на приобретение</h2>
            
            <Form form={form} onFinish={onFinish} layout="vertical">
                <Item label="Получатели" name="recipients" rules={[{ required: true, message: 'Обязательное поле' }]}>
                    <Select mode="multiple" placeholder="Выберите сотрудников..." size="large" 
                    allowClear
           filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }>
                         {recipientsList?.map((recipient) => (
                                <Option key={recipient.id} value={recipient.id} label={recipient.fullname}>
                                {recipient.fullname} {!!recipient.position && <>- <span style={{color: 'gray', fontSize: 12}}>{recipient.position}</span></>}
                                </Option>
                            ))}
                    </Select>
                </Item>

                <Item label="Заявка на имя" name="docTitle" rules={[{required: true, message: 'Обязательное поле'}]}>
                    <Select placeholder="Кому адресована заявка?" size="large">
                        {documentTitle1.map((title) => (
                            <Option key={title} value={title}>{title}</Option>
                        ))}
                    </Select>
                </Item>

                {prop.referenceId ? (
                    <div style={{ marginBottom: 24, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, color: '#166534', fontSize: 13, fontWeight: 600 }}>
                        <LinkOutlined /> На основании: Акта тех. экспертизы №{prop.referenceId.slice(0,8)}...
                    </div>
                ) : (
                    <Item label="Акт тех. экспертизы (основание)" name="reference" rules={[{ required: true, message: 'Обязательное поле' }]}>
                        <Select placeholder="Выберите акт..." size="large">
                            {docListFiltred.map((doc, index) => (
                                <Option key={doc.id} value={index}>
                                    {doc.documentTitle} (от {new Date(doc.time).toLocaleDateString()})
                                </Option>
                            ))}
                        </Select>
                    </Item>
                )}

                <Item label="Текст заявки" name="regtext" rules={[{ required: true, message: 'Обязательное поле' }]}>
                    <TextArea placeholder="Опишите, что требуется приобрести..." rows={5}/>
                </Item>

                <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={mutationLoading}
                    size="large"
                    icon={<SendOutlined style={{ display: 'inline-flex' }} />}
                    style={{ width: '100%', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    Создать заявку
                </Button>
            </Form>
        </div>
    )
}

export default Template;
